import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { API_URL } from '../../config';

function OpenAISettings() {
  const [isLoading, setIsLoading] = useState(true);
  const [openaiConfig, setOpenaiConfig] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form fields
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('gpt-4-turbo');
  const [temperature, setTemperature] = useState(0.7);
  
  // UI states
  const [showApiKey, setShowApiKey] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  useEffect(() => {
    fetchOpenAIConfig();
  }, []);
  
  const fetchOpenAIConfig = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/openai`);
      console.log('OpenAI config response:', response.data);
      
      const config = response.data.openaiConfig;
      setOpenaiConfig(config);
      
      if (config) {
        setApiKey(config.api_key || '');
        setModel(config.model || 'gpt-4-turbo');
        setTemperature(parseFloat(config.temperature) || 0.7);
      }
      
      setError('');
    } catch (err) {
      console.error('Error fetching OpenAI config:', err);
      setError(`Failed to load OpenAI configuration: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setError('');
      setSuccess('');
      setIsSubmitting(true);
      
      console.log('Submitting OpenAI config:', { apiKey, model, temperature });
      
      const response = await axios.post(`${API_URL}/openai`, {
        apiKey,
        model,
        temperature
      });
      
      console.log('OpenAI update response:', response.data);
      
      setSuccess('OpenAI settings updated successfully');
      
      // Refresh config
      await fetchOpenAIConfig();
    } catch (err) {
      console.error('Error updating OpenAI config:', err);
      setError(`Error updating OpenAI configuration: ${err.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleTestConnection = async () => {
    try {
      setTestResult(null);
      setIsTesting(true);
      
      console.log('Testing OpenAI connection with API key:', apiKey ? 'Provided' : 'Missing');
      
      const response = await axios.post(`${API_URL}/openai/test`, {
        apiKey: apiKey,
        model: model
      });
      
      console.log('OpenAI test response:', response.data);
      
      setTestResult({
        success: response.data.success,
        message: response.data.message,
        response: response.data.response
      });
    } catch (err) {
      console.error('Error testing OpenAI connection:', err);
      setTestResult({
        success: false,
        message: err.response?.data?.message || `Connection failed: ${err.message}`
      });
    } finally {
      setIsTesting(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading OpenAI configuration...</p>
      </div>
    );
  }
  
  return (
    <>
      <Card className="shadow-sm">
        <Card.Header>
          <h5 className="m-0">OpenAI Configuration</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>API Key</Form.Label>
              <div className="input-group">
                <Form.Control 
                  type={showApiKey ? "text" : "password"} 
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  required
                />
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  <FontAwesomeIcon icon={showApiKey ? faEyeSlash : faEye} />
                </Button>
              </div>
              <Form.Text className="text-muted">
                Enter your OpenAI API key. This will be used for all AI interactions.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Model</Form.Label>
              <Form.Select 
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Select the OpenAI model to use. GPT-4 provides better results but may be more expensive.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Temperature: {temperature}</Form.Label>
              <Form.Range 
                min={0} 
                max={2} 
                step={0.1}
                value={temperature}
                onChange={(e) => setTemperature(parseFloat(e.target.value))}
              />
              <div className="d-flex justify-content-between">
                <span>Precise (0.0)</span>
                <span>Creative (2.0)</span>
              </div>
              <Form.Text className="text-muted">
                Adjust the creativity of responses. Lower values are more deterministic, higher values are more random.
              </Form.Text>
            </Form.Group>
            
            <div className="d-flex gap-2">
              <Button 
                type="submit" 
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Settings'}
              </Button>
              
              <Button 
                type="button" 
                variant="success"
                onClick={() => setShowTestModal(true)}
              >
                Test Connection
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
      
      {/* Test Connection Modal */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Test OpenAI Connection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This will send a test query to OpenAI to verify your connection and API key.</p>
          
          <div className="mb-3">
            <Form.Label>API Key</Form.Label>
            <Form.Control 
              type="password" 
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              required
            />
          </div>
          
          <div className="mb-3">
            <Form.Label>Model</Form.Label>
            <Form.Select 
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              <option value="gpt-4-turbo">GPT-4 Turbo</option>
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </Form.Select>
          </div>
          
          {isTesting && (
            <div className="text-center my-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Testing connection...</span>
              </div>
              <p className="mt-2">Testing connection to OpenAI...</p>
            </div>
          )}
          
          {testResult && (
            <Alert variant={testResult.success ? 'success' : 'danger'}>
              <Alert.Heading>{testResult.success ? 'Success!' : 'Error'}</Alert.Heading>
              <p>{testResult.message}</p>
              {testResult.success && testResult.response && (
                <div className="mt-2">
                  <strong>Sample response:</strong>
                  <p className="mt-1">{testResult.response}</p>
                </div>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTestModal(false)}>
            Close
          </Button>
          <Button 
            variant="success" 
            onClick={handleTestConnection}
            disabled={isTesting || !apiKey}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default OpenAISettings;
